// T073: Create post service for dsteem SDK
// T089: Update post via dsteem SDK
// T093: Delete post via dsteem SDK

import { PrivateKey, type Operation } from '@steempro/dsteem'
import { getSteemitClient } from './client'
import { generatePermlink } from '@/utils/permlink'
import { generateTitle } from '@/utils/titleGenerator'
import type { PostMetadata } from '@/types/post'

export interface CreatePostParams {
  author: string
  postingKey: string
  title?: string
  body: string
  tags?: string[]
  images?: string[]
}

export interface UpdatePostParams {
  author: string
  postingKey: string
  permlink: string
  title?: string
  body: string
  tags?: string[]
  images?: string[]
}

export interface DeletePostParams {
  author: string
  postingKey: string
  permlink: string
}

const DEFAULT_TAG = 'fast-steemit'
const APP_NAME = 'fast-steemit/0.1.0'

/**
 * Creates a new post on Steemit blockchain
 */
export async function createPost(params: CreatePostParams): Promise<{
  success: boolean
  permlink?: string
  error?: string
}> {
  try {
    const { author, postingKey, body, images = [] } = params

    // Auto-generate title if not provided (T076)
    const title = params.title || generateTitle(body)

    // Use provided tags or default tag if none provided
    const tags = params.tags && params.tags.length > 0 ? params.tags : [DEFAULT_TAG]

    // Generate unique permlink
    const permlink = generatePermlink(title)

    // Prepare metadata
    const metadata: PostMetadata = {
      tags,
      app: APP_NAME,
      format: 'markdown',
    }

    if (images.length > 0) {
      metadata.image = images
    }

    // Use first tag as category
    const parentPermlink = tags[0] || DEFAULT_TAG

    // Create the post operation
    const operation = [
      'comment',
      {
        parent_author: '',
        parent_permlink: parentPermlink,
        author,
        permlink,
        title,
        body,
        json_metadata: JSON.stringify(metadata),
      },
    ]

    // Broadcast to blockchain
    const client = getSteemitClient()
    const privateKey = PrivateKey.fromString(postingKey)

    const result = await client.broadcast.sendOperations(
      [operation as unknown as Operation],
      privateKey
    )

    if (result) {
      return {
        success: true,
        permlink,
      }
    }

    return {
      success: false,
      error: 'Failed to broadcast post',
    }
  } catch (error) {
    console.error('Error creating post:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Updates an existing post on Steemit blockchain
 * Must be within 7 days of original post (T088)
 */
export async function updatePost(params: UpdatePostParams): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const { author, postingKey, permlink, body, images = [] } = params

    // Check if post can be edited (T088)
    const client = getSteemitClient()
    const post = await client.database.call('get_content', [author, permlink])

    if (!post || !post.author) {
      return {
        success: false,
        error: 'Post not found',
      }
    }

    const created = new Date(post.created)
    const now = new Date()
    const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)

    if (daysSinceCreation > 7) {
      return {
        success: false,
        error: 'Cannot edit posts older than 7 days',
      }
    }

    // Get existing metadata
    let metadata: PostMetadata
    try {
      metadata = JSON.parse(post.json_metadata || '{}')
    } catch {
      metadata = {
        tags: [],
        app: APP_NAME,
        format: 'markdown',
      }
    }

    // Update tags if provided
    if (params.tags) {
      metadata.tags = params.tags
    }

    // Update images if provided
    if (images.length > 0) {
      metadata.image = images
    }

    // Update title if provided
    const title = params.title || post.title || generateTitle(body)

    // Create the update operation
    const operation = [
      'comment',
      {
        parent_author: post.parent_author,
        parent_permlink: post.parent_permlink,
        author,
        permlink,
        title,
        body,
        json_metadata: JSON.stringify(metadata),
      },
    ]

    // Broadcast to blockchain
    const privateKey = PrivateKey.fromString(postingKey)
    const result = await client.broadcast.sendOperations([operation as unknown as Operation], privateKey)

    if (result) {
      return { success: true }
    }

    return {
      success: false,
      error: 'Failed to update post',
    }
  } catch (error) {
    console.error('Error updating post:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Deletes a post from Steemit blockchain
 * Only works if post has no comments and is within 7 days (T092)
 */
export async function deletePost(params: DeletePostParams): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const { author, postingKey, permlink } = params

    // Check delete constraints (T092)
    const client = getSteemitClient()
    const post = await client.database.call('get_content', [author, permlink])

    if (!post || !post.author) {
      return {
        success: false,
        error: 'Post not found',
      }
    }

    // Check if post has comments
    if (post.children > 0) {
      return {
        success: false,
        error: 'Cannot delete posts with comments',
      }
    }

    // Check 7-day window
    const created = new Date(post.created)
    const now = new Date()
    const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)

    if (daysSinceCreation > 7) {
      return {
        success: false,
        error: 'Cannot delete posts older than 7 days',
      }
    }

    // Create the delete operation
    const operation = [
      'delete_comment',
      {
        author,
        permlink,
      },
    ]

    // Broadcast to blockchain
    const privateKey = PrivateKey.fromString(postingKey)
    const result = await client.broadcast.sendOperations([operation as unknown as Operation], privateKey)

    if (result) {
      return { success: true }
    }

    return {
      success: false,
      error: 'Failed to delete post',
    }
  } catch (error) {
    console.error('Error deleting post:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Checks if a post can be edited
 */
export async function canEditPost(author: string, permlink: string): Promise<boolean> {
  try {
    const client = getSteemitClient()
    const post = await client.database.call('get_content', [author, permlink])

    if (!post || !post.author) {
      return false
    }

    const created = new Date(post.created)
    const now = new Date()
    const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)

    return daysSinceCreation <= 7
  } catch {
    return false
  }
}

/**
 * Checks if a post can be deleted
 */
export async function canDeletePost(author: string, permlink: string): Promise<{
  canDelete: boolean
  reason?: string
}> {
  try {
    const client = getSteemitClient()
    const post = await client.database.call('get_content', [author, permlink])

    if (!post || !post.author) {
      return {
        canDelete: false,
        reason: 'Post not found',
      }
    }

    // Check if post has comments
    if (post.children > 0) {
      return {
        canDelete: false,
        reason: 'Cannot delete posts with comments',
      }
    }

    // Check 7-day window
    const created = new Date(post.created)
    const now = new Date()
    const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)

    if (daysSinceCreation > 7) {
      return {
        canDelete: false,
        reason: 'Cannot delete posts older than 7 days',
      }
    }

    return { canDelete: true }
  } catch {
    return {
      canDelete: false,
      reason: 'Error checking post',
    }
  }
}
